// ──────────────────────────────────────────────
// GeoDrops — Map Component (Leaflet)
// Neon Navigator design: pulsing ring pins,
// gradient FAB, dark-tinted map tiles.
// ──────────────────────────────────────────────

import { useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import type { Pin } from "@/types";
import { getCategoryMeta } from "@/types";

interface MapProps {
  center: { lat: number; lng: number };
  pins: Pin[];
  onPinClick: (pin: Pin) => void;
  onDropPin: (lat: number, lng: number) => void;
  showDropButton: boolean;
}

// Neon Navigator pulsing-ring pin
function createPinIcon(pin: Pin): L.DivIcon {
  const meta = getCategoryMeta(pin.category);
  return L.divIcon({
    className: "geodrops-marker",
    html: `<div style="position:relative;width:48px;height:48px;display:flex;align-items:center;justify-content:center;">
      <div style="position:absolute;width:48px;height:48px;border-radius:50%;background:${meta.color}20;animation:pulse-ring 2s infinite;"></div>
      <div style="position:absolute;width:36px;height:36px;border-radius:50%;background:${meta.color}30;"></div>
      <div style="
        position:relative;z-index:1;
        width:40px;height:40px;
        background:#132156;
        border:2px solid ${meta.color};
        border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        font-size:20px;
        box-shadow:0 0 16px ${meta.color}40, 0 4px 12px rgba(0,0,0,0.4);
      ">${meta.emoji}</div>
    </div>`,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
    popupAnchor: [0, -28],
  });
}

// User location — electric blue dot with glow
function createUserIcon(): L.DivIcon {
  return L.divIcon({
    className: "geodrops-user-marker",
    html: `<div style="position:relative;width:22px;height:22px;display:flex;align-items:center;justify-content:center;">
      <div style="position:absolute;width:22px;height:22px;border-radius:50%;background:rgba(133,173,255,0.3);animation:pulse-ring 2s infinite;"></div>
      <div style="
        width:14px;height:14px;
        background:#85adff;
        border:3px solid #020a2f;
        border-radius:50%;
        box-shadow:0 0 12px rgba(133,173,255,0.6);
      "></div>
    </div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

export function Map({ center, pins, onPinClick, onDropPin, showDropButton }: MapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const userMarkerRef = useRef<L.Marker | null>(null);

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [center.lat, center.lng],
      zoom: 15,
      zoomControl: false,
      attributionControl: false,
    });

    // Use dark CartoDB tiles for neon contrast
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      maxZoom: 19,
    }).addTo(map);

    L.control.zoom({ position: "topright" }).addTo(map);
    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Update user location marker
  useEffect(() => {
    if (!mapRef.current) return;
    if (userMarkerRef.current) {
      userMarkerRef.current.setLatLng([center.lat, center.lng]);
    } else {
      userMarkerRef.current = L.marker([center.lat, center.lng], {
        icon: createUserIcon(),
        zIndexOffset: 1000,
      }).addTo(mapRef.current);
    }
  }, [center.lat, center.lng]);

  // Update pin markers
  useEffect(() => {
    if (!markersRef.current || !mapRef.current) return;
    markersRef.current.clearLayers();
    pins.forEach((pin) => {
      const marker = L.marker([pin.lat, pin.lng], { icon: createPinIcon(pin) });
      marker.on("click", () => onPinClick(pin));
      markersRef.current!.addLayer(marker);
    });
  }, [pins, onPinClick]);

  const recenter = useCallback(() => {
    mapRef.current?.setView([center.lat, center.lng], 15, { animate: true });
  }, [center.lat, center.lng]);

  const handleDrop = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    onDropPin(c.lat, c.lng);
  }, [onDropPin]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

      {/* Crosshair */}
      {showDropButton && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          <div style={{
            width: 24, height: 24,
            borderRadius: "50%",
            border: "2px solid #85adff",
            background: "rgba(133,173,255,0.15)",
            boxShadow: "0 0 20px rgba(133,173,255,0.3)",
          }} />
        </div>
      )}

      {/* FABs — right side */}
      <div
        style={{
          position: "absolute",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)",
          right: 16,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          zIndex: 1000,
        }}
      >
        {/* Recenter */}
        <button
          onClick={recenter}
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "rgba(14,27,76,0.85)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(61,70,108,0.3)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
            fontSize: 18,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#85adff",
          }}
          aria-label="Re-center map"
        >
          &#8982;
        </button>

        {/* Drop pin FAB — gradient per Neon Navigator */}
        {showDropButton && (
          <button
            onClick={handleDrop}
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #85adff, #6c9fff)",
              border: "none",
              boxShadow: "0 12px 32px rgba(0,0,0,0.4), 0 0 0 4px rgba(133,173,255,0.2)",
              fontSize: 28,
              fontWeight: 300,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#002c65",
              transition: "transform 0.15s",
            }}
            aria-label="Drop a pin here"
          >
            +
          </button>
        )}
      </div>
    </div>
  );
}
